/**
 * The two questions the dashboard has to answer the same way everywhere: what a
 * position costs per month, and whether it counts against leverage.
 *
 * They live outside the component because the header, the OfferCalculator and
 * the decision panel each used to answer them separately, and disagreed on the
 * same screen.
 */

export interface WithdrawalLeverage {
  amount: number;
  frequency: string;
  monthly_payout?: number;
  is_active: boolean;
  include_in_leverage?: boolean;
}

export const freqToMonthlyMultiplier: Record<string, number> = {
  daily: 20,
  weekly: 4,
  biweekly: 2,
  monthly: 1,
  // No schedule to multiply. The backend sends an observed rate as
  // monthly_payout for these; there is nothing to derive one from here.
  unknown: 0,
};

function monthlyMultiplier(frequency: string, businessDaysPerMonth = 20): number {
  if (frequency === "daily") {
    return Number.isFinite(businessDaysPerMonth) && businessDaysPerMonth > 0
      ? businessDaysPerMonth
      : 20;
  }
  return freqToMonthlyMultiplier[frequency] ?? 0;
}

/**
 * Monthly obligation for one withdrawal cluster. Prefers the backend's figure,
 * which is the only one that can be right for an unknown cadence. The fallback
 * is 0, never a default multiplier: treating an unrecognized cadence as daily
 * invented twenty payments a month out of nothing.
 *
 * `cadenceOverride` is a reviewer putting an unknown position on a schedule by
 * hand. It replaces the backend's observed rate rather than deferring to it —
 * the point of the choice is to price the position as that schedule.
 */
export function withdrawalMonthlyPayout(
  w: Pick<WithdrawalLeverage, "amount" | "frequency" | "monthly_payout">,
  cadenceOverride?: string,
  businessDaysPerMonth = 20
): number {
  if (cadenceOverride) {
    return w.amount * monthlyMultiplier(cadenceOverride, businessDaysPerMonth);
  }
  return w.monthly_payout ?? w.amount * monthlyMultiplier(w.frequency, businessDaysPerMonth);
}

/**
 * Whether a position counts toward leverage, which is NOT the same question as
 * whether it is still pulling. An underwriter can exclude a settled position
 * the statements still show pulls for; the status dot keeps saying "active"
 * while the payout drops out. Reading `is_active` here made the header and the
 * OfferCalculator disagree with the decision panel.
 *
 * `localOverride` is the click-to-toggle state the dashboard keeps for its own
 * what-if math, and wins while it is set.
 */
export function withdrawalInLeverage(
  w: Pick<WithdrawalLeverage, "is_active" | "include_in_leverage">,
  localOverride?: boolean
): boolean {
  return localOverride ?? w.include_in_leverage ?? w.is_active;
}
